// Shared helpers for the Umpiring Assignments picking board:
//
//   • buildAnnouncement(scope, entries)   — text-only announcement body
//                                           (Title + Body) suitable for
//                                           POST /announcements.
//   • shareNode(node, summary, filename?) — snapshot a DOM node and
//                                           route through the native
//                                           share sheet / clipboard /
//                                           download (three-tier
//                                           fallback, same pattern as
//                                           AssignmentsFlyer.jsx).
//
// The "entries" argument to buildAnnouncement is an array of:
//   { match, umpires }
// where match is a row from api.listUpcomingMatches() and umpires is
// the assigned-only list from nomsById (each with firstName/lastName/
// phone/teamName). Sorted by date+time by the caller.

// html2canvas-pro is a drop-in replacement that supports modern CSS
// color functions — color-mix(), oklch(), color(), lab(), lch(). Our
// tone system leans heavily on color-mix(), which the 1.x html2canvas
// parser fails on ("unsupported color function 'color'").
import html2canvas from "html2canvas-pro";
import { fmtMatchDate, fmt12 } from "../shared";

function formatUmpire(u) {
  const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Umpire";
  return u.phone
    ? `${name} — ${u.phone}`
    : `${name}`;
}

export function buildAnnouncement(scope, entries) {
  const title = entries.length === 1
    ? `Umpiring Assignment · ${scope}`
    : `Umpiring Assignments · ${scope}`;

  if (entries.length === 0) {
    return {
      title,
      body: `No confirmed umpire assignments for ${scope} yet.`
    };
  }

  const chunks = entries.map(({ match, umpires }) => {
    const when = `${fmtMatchDate(match.date)}${match.time ? ` · ${fmt12(match.time)} ET` : ""}`;
    const lines = [
      when,
      `${match.ground}`,
      match.series ? `${match.series}` : null,
      `${match.teamOne} vs ${match.teamTwo}`,
      "Umpires:",
      ...(umpires.length > 0
        ? umpires.map(u => `  • ${formatUmpire(u)}`)
        : ["  • (no umpires assigned)"])
    ].filter(Boolean);
    return lines.join("\n");
  });

  const header = `Umpiring Assignments · ${scope}`;
  const rule = "─".repeat(28);
  const body = [header, rule, "", chunks.join("\n\n"), "", rule, "Please contact the umpires directly if the assignment needs to change."].join("\n");

  return { title, body };
}

// Three-tier share for a DOM node:
//   1. Web Share API with files  (mobile share sheet — WhatsApp, Mail…)
//   2. Clipboard image           (desktop paste target)
//   3. PNG download              (final fallback)
export async function shareNode(node, summary, filename = "umpiring-assignments.png") {
  if (!node) throw new Error("Nothing to share.");
  const canvas = await html2canvas(node, {
    backgroundColor: "#ffffff",
    scale: 3,
    useCORS: true,
    logging: false
  });
  const blob = await new Promise(r => canvas.toBlob(r, "image/png"));
  const file = new File([blob], filename, { type: "image/png" });

  if (typeof navigator !== "undefined"
      && typeof navigator.canShare === "function"
      && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        text: summary,
        title: "Umpiring Assignments"
      });
      return { shared: true };
    } catch (err) {
      if (err?.name === "AbortError") return { cancelled: true };
    }
  }

  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      return { clipboard: true };
    }
  } catch { /* fall through */ }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { downloaded: true };
}
